import exportLoadingIcon from '@/assets/icons/loading@2x.png';
import { DevicePoint, exportAggregateHistoryData } from '@/api/history';
import { DataPropertyType } from '@/shared/types';
import { CheckCircleFilled, UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Form, Modal, Radio } from '@maxtropy/components';
import { Col, Divider, Progress, Row, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DatePickerType,
  getDisabledDateByMode,
  getTimeDistance,
  getTsRange,
  OriginalType,
  partitionToPicker,
  RangePickerValue,
  StatisticsPartition,
} from '../DateSwitch';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;
const CheckboxGroup = Checkbox.Group;

type ExportStatus = 'exporting' | 'success';

export interface AggregateExportProps {
  activePoint?: DevicePoint;
  dateMode: StatisticsPartition;
  deviceId?: number;
  devicePoints: DevicePoint[];
  disabled: boolean;
  pointType: number;
  rangePickerValue: RangePickerValue;
}

export const partitionToTimeSolution: Record<StatisticsPartition, 'ORIGINAL' | 'DAY' | 'MONTH' | 'YEAR'> = {
  [DatePickerType.DAY]: 'DAY',
  [DatePickerType.MONTH]: 'MONTH',
  [DatePickerType.YEAR]: 'YEAR',
  [OriginalType.ORIGINAL]: 'ORIGINAL',
  [OriginalType.ORIGINALENUM]: 'ORIGINAL',
};

const aggregateExportRangeLimit: Record<StatisticsPartition, { distance: number; message: string }> = {
  [DatePickerType.DAY]: { distance: 30, message: '日维度最大跨度为31天' },
  [DatePickerType.MONTH]: { distance: 11, message: '月维度最大跨度为12个月' },
  [DatePickerType.YEAR]: { distance: 8, message: '年维度最大跨度为9年' },
  [OriginalType.ORIGINAL]: { distance: 9, message: '原始数据维度最多选择10天' },
  [OriginalType.ORIGINALENUM]: { distance: 9, message: '原始数据维度最多选择10天' },
};

const AggregateExport: React.FC<AggregateExportProps> = props => {
  const { activePoint, dateMode, deviceId, devicePoints, disabled, pointType, rangePickerValue } = props;
  const [showExportModal, setShowExportModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus>('exporting');
  const [exportProgress, setExportProgress] = useState<number>();
  const [exportFile, setExportFile] = useState<{ blob: Blob; fileName?: string }>();
  const [exportDateMode, setExportDateMode] = useState<StatisticsPartition>(dateMode);
  const [exportRangePickerValue, setExportRangePickerValue] = useState<RangePickerValue>();
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const exportAbortController = useRef<AbortController>();
  const exportProgressTimer = useRef<number>();
  const exportCompleteTimer = useRef<number>();

  useEffect(
    () => () => {
      exportAbortController.current?.abort();
      window.clearInterval(exportProgressTimer.current);
      window.clearTimeout(exportCompleteTimer.current);
    },
    []
  );

  const checkDataList = useMemo(() => {
    return devicePoints.map(item => ({
      ...item,
      disabled:
        ![OriginalType.ORIGINALENUM, OriginalType.ORIGINAL].includes(exportDateMode as any) &&
        item.type === DataPropertyType.ENUM,
    }));
  }, [devicePoints, exportDateMode]);

  useEffect(() => {
    if (showExportModal && activePoint?.id) {
      const filters = checkDataList.filter(item => !item.disabled && item.id === activePoint.id);
      setCheckedList(filters.map(item => item.id));
    }
  }, [activePoint, checkDataList, showExportModal]);

  const getRangeError = () => {
    if (!exportRangePickerValue?.[0] || !exportRangePickerValue?.[1]) return '请选择统计时间';
    const [start, end] = exportRangePickerValue;
    const unit =
      exportDateMode === DatePickerType.MONTH ? 'month' : exportDateMode === DatePickerType.YEAR ? 'year' : 'day';
    const distance = Math.abs(end.startOf(unit).diff(start.startOf(unit), unit));
    const rangeLimit = aggregateExportRangeLimit[exportDateMode];
    if (distance <= rangeLimit.distance) return undefined;
    return rangeLimit.message;
  };

  const getFileName = (disposition: string | null) => {
    const encodedFileName = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const normalFileName = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
    const fileName = encodedFileName || normalFileName;
    if (!fileName) return undefined;
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  };

  const downloadFile = (file: { blob: Blob; fileName?: string }) => {
    const url = window.URL.createObjectURL(file.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName!;
    link.addEventListener('click', event => event.stopPropagation());
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleExport = async () => {
    if (exportAbortController.current) return;
    const rangeError = getRangeError();
    if (rangeError) {
      Modal.error({ title: '导出失败', content: rangeError });
      return;
    }
    if (!deviceId || checkedList.length === 0 || !exportRangePickerValue) return;

    const { tsStart, tsEnd } = getTsRange(exportRangePickerValue, exportDateMode);
    const controller = new AbortController();
    exportAbortController.current = controller;
    window.clearInterval(exportProgressTimer.current);
    window.clearTimeout(exportCompleteTimer.current);
    setShowProgressModal(true);
    setExportStatus('exporting');
    setExportProgress(0);
    setExportFile(undefined);
    exportProgressTimer.current = window.setInterval(() => {
      setExportProgress(current => {
        const progress = current ?? 0;
        const increment = progress < 50 ? 8 : progress < 80 ? 4 : 1;
        return Math.min(progress + increment, 95);
      });
    }, 300);

    try {
      const { blob, disposition } = await exportAggregateHistoryData(
        {
          deviceId,
          timeSolution: partitionToTimeSolution[exportDateMode],
          type: pointType,
          sortOrder,
          startTime: tsStart,
          endTime: tsEnd,
          propertyId: checkedList,
        },
        controller.signal
      );
      setExportFile({ blob, fileName: getFileName(disposition) });
      window.clearInterval(exportProgressTimer.current);
      setExportProgress(100);
      exportCompleteTimer.current = window.setTimeout(() => setExportStatus('success'), 300);
    } catch (error) {
      window.clearInterval(exportProgressTimer.current);
      if (controller.signal.aborted || (error as Error).name === 'AbortError') {
        setShowProgressModal(false);
        return;
      }
      setShowProgressModal(false);
      Modal.error({
        title: '导出失败',
        content: (error as Error).message || '导出失败，请稍后重试',
      });
    } finally {
      if (exportAbortController.current === controller) exportAbortController.current = undefined;
    }
  };

  const handleDownload = () => {
    if (!exportFile) return;
    downloadFile(exportFile);
    setShowExportModal(false);
    setShowProgressModal(false);
  };

  const checkAll = checkDataList.filter(item => !item.disabled).length === checkedList.length;
  const indeterminate =
    checkedList.length > 0 && checkedList.length < checkDataList.filter(item => !item.disabled).length;

  return (
    <>
      <Button
        type="primary"
        disabled={disabled}
        onClick={() => {
          setExportDateMode(dateMode);
          setExportRangePickerValue(rangePickerValue);
          setShowExportModal(true);
        }}
        icon={<UploadOutlined />}
      >
        聚合导出
      </Button>
      {showExportModal && (
        <Modal
          title="选择聚合导出数据"
          open={true}
          onOk={async () => {
            if (!checkedList.length) {
              Modal.error({ title: '导出失败', content: '所选择的数据属性均为空，请重新选择！' });
              return;
            }
            await handleExport();
          }}
          onCancel={() => {
            setShowExportModal(false);
            setCheckedList([]);
            setExportRangePickerValue(undefined);
          }}
        >
          <Form.Item label="导出方式">
            <Radio.Group value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
              <Radio value="DESC">按时间倒序</Radio>
              <Radio value="ASC">按时间正序</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="查询维度">
            <Radio.Group
              value={exportDateMode}
              onChange={(e: RadioChangeEvent) => {
                const nextMode = e.target.value as StatisticsPartition;
                setExportDateMode(nextMode);
                setExportRangePickerValue(getTimeDistance(nextMode));
              }}
            >
              <Radio disabled={activePoint?.type !== DataPropertyType.YC} value={DatePickerType.DAY}>
                日
              </Radio>
              <Radio disabled={activePoint?.type !== DataPropertyType.YC} value={DatePickerType.MONTH}>
                月
              </Radio>
              <Radio disabled={activePoint?.type !== DataPropertyType.YC} value={DatePickerType.YEAR}>
                年
              </Radio>
              <Radio
                value={
                  pointType === 1
                    ? activePoint?.type === DataPropertyType.YC
                      ? OriginalType.ORIGINAL
                      : OriginalType.ORIGINALENUM
                    : OriginalType.ORIGINAL
                }
              >
                原始数据
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="统计时间">
            <RangePicker
              style={{ width: 294 }}
              allowClear={false}
              picker={partitionToPicker[exportDateMode]}
              value={exportRangePickerValue}
              onChange={dates => setExportRangePickerValue(dates)}
              disabledDate={getDisabledDateByMode(
                exportDateMode,
                Object.fromEntries(
                  Object.entries(aggregateExportRangeLimit).map(([mode, limit]) => [mode, limit.distance])
                )
              )}
            />
          </Form.Item>
          <Checkbox
            indeterminate={indeterminate}
            onChange={(e: CheckboxChangeEvent) =>
              setCheckedList(e.target.checked ? checkDataList.filter(item => !item.disabled).map(item => item.id) : [])
            }
            checked={checkAll}
          >
            全选
          </Checkbox>
          <Divider style={{ margin: '12px 0' }} />
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <CheckboxGroup value={checkedList} onChange={setCheckedList}>
              <Row>
                {checkDataList.map(item => (
                  <Col span={24} key={item.id}>
                    <div style={{ paddingBottom: 8 }}>
                      <Checkbox disabled={item.disabled} value={item.id}>
                        <Space size={8}>
                          <span>{item.name}</span>
                          <span style={{ fontSize: 12, color: 'red' }}>
                            {item.disabled ? '该类型数据只支持导出"原始数据"维度数据！' : ''}
                          </span>
                        </Space>
                      </Checkbox>
                    </div>
                  </Col>
                ))}
              </Row>
            </CheckboxGroup>
          </div>
        </Modal>
      )}
      {showProgressModal && (
        <Modal
          title={null}
          open={true}
          closable={false}
          maskClosable={false}
          keyboard={false}
          width={480}
          className={styles.exportProgressModal}
          onCancel={() => setShowProgressModal(false)}
          footer={null}
        >
          <div style={{ padding: '8px 0 0 8px' }}>
            <Space size={16} align="start" style={{ width: '100%' }}>
              {exportStatus === 'success' ? (
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 21, marginTop: 2 }} />
              ) : (
                <img className={styles.exportLoadingIcon} src={exportLoadingIcon} alt="正在导出" />
              )}
              <div>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                  {exportStatus === 'success' ? '数据导出完成' : '正在导出数据'}
                </div>
                {exportStatus === 'exporting' && (
                  <>
                    <Progress percent={exportProgress ?? 0} status="active" />
                    <div style={{ color: 'var(--mx-text-secondary-color)', marginTop: 16 }}>
                      导出过程中请勿关闭页面，以免影响数据完整性
                    </div>
                  </>
                )}
                {exportStatus === 'success' && (
                  <div style={{ color: 'var(--mx-text-secondary-color)' }}>文件已准备就绪，点击下载保存到本地</div>
                )}
              </div>
            </Space>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <Space size={8}>
                {exportStatus === 'exporting'
                  ? [
                      <Button
                        key="cancel"
                        onClick={() => {
                          if (exportAbortController.current) {
                            exportAbortController.current.abort();
                          } else {
                            window.clearTimeout(exportCompleteTimer.current);
                            setShowProgressModal(false);
                          }
                        }}
                      >
                        取消导出
                      </Button>,
                      <Button key="exporting" type="primary" loading disabled>
                        导出中
                      </Button>,
                    ]
                  : [
                      <Button key="close" onClick={() => setShowProgressModal(false)}>
                        关闭
                      </Button>,
                      <Button key="download" type="primary" onClick={handleDownload}>
                        下载文件
                      </Button>,
                    ]}
              </Space>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AggregateExport;
